import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, 
  Filter, 
  UserCheck, 
  UserX, 
  Mail, 
  Phone,
  MapPin,
  MoreVertical,
  Trash2,
  Eye,
  ChevronDown,
  FileText,
  Download,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  qualified: { bg: "bg-[#1A4D2E]/10", text: "text-[#1A4D2E]", label: "Qualified" },
  disqualified: { bg: "bg-[#A94442]/10", text: "text-[#A94442]", label: "Disqualified" },
  new: { bg: "bg-[#FDB813]/10", text: "text-amber-700", label: "New" },
  contacted: { bg: "bg-blue-100", text: "text-blue-700", label: "Contacted" },
  converted: { bg: "bg-purple-100", text: "text-purple-700", label: "Converted" }
};

const bookingStatusStyles = {
  confirmed: { bg: "bg-[#1A4D2E]/10", text: "text-[#1A4D2E]", label: "Confirmed", icon: CheckCircle },
  requested: { bg: "bg-[#FDB813]/10", text: "text-amber-700", label: "Requested", icon: Clock },
  pending: { bg: "bg-slate-100", text: "text-slate-500", label: "Pending", icon: Clock },
  none: { bg: "", text: "text-slate-400", label: "No Booking", icon: null }
};

const LeadRow = ({ lead, onView, onDelete, onStatusChange }) => {
  const style = statusStyles[lead.status] || statusStyles.new;
  const bookingStyle = lead.booking_status 
    ? bookingStatusStyles[lead.booking_status] 
    : bookingStatusStyles.none;
  const BookingIcon = bookingStyle.icon;
  
  const getScoreColor = (score) => {
    if (score >= 70) return "text-[#1A4D2E]";
    if (score >= 40) return "text-amber-600";
    return "text-[#A94442]";
  };

  return (
    <tr className="lead-row border-b border-slate-100" data-testid={`lead-row-${lead.id}`}>
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-slate-600">
              {lead.first_name[0]}{lead.last_name[0]}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-900">{lead.first_name} {lead.last_name}</p>
              {lead.is_test && (
                <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded font-medium">🧪 TEST</span>
              )}
            </div>
            <p className="text-sm text-slate-500">{lead.email}</p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-1.5 text-slate-600">
          <Phone className="w-4 h-4" />
          <span className="text-sm">{lead.phone}</span>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-1.5 text-slate-600">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">{lead.city}, {lead.state}</span>
        </div>
      </td>
      <td className="py-4 px-4">
        <span className={`text-lg font-bold ${getScoreColor(lead.score)}`}>
          {lead.score}
        </span>
      </td>
      <td className="py-4 px-4">
        <Badge className={`${style.bg} ${style.text} border-0 font-medium`}>
          {lead.is_qualified ? <UserCheck className="w-3 h-3 mr-1" /> : <UserX className="w-3 h-3 mr-1" />}
          {style.label}
        </Badge>
      </td>
      <td className="py-4 px-4">
        {lead.booking_status ? (
          <Badge className={`${bookingStyle.bg} ${bookingStyle.text} border-0 font-medium`}>
            {BookingIcon && <BookingIcon className="w-3 h-3 mr-1" />}
            {bookingStyle.label}
          </Badge>
        ) : (
          <span className="text-sm text-slate-400">—</span>
        )}
      </td>
      <td className="py-4 px-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid={`lead-actions-${lead.id}`}>
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onView(lead)} data-testid={`view-lead-${lead.id}`}>
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(lead, "contacted")} data-testid={`contact-lead-${lead.id}`}>
              <Phone className="w-4 h-4 mr-2" />
              Mark Contacted
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onStatusChange(lead, "converted")} data-testid={`convert-lead-${lead.id}`}>
              <UserCheck className="w-4 h-4 mr-2" />
              Mark Converted
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onDelete(lead.id)} 
              className="text-red-600"
              data-testid={`delete-lead-${lead.id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};

export default function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (lead, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { status: newStatus });
      toast.success(`Lead marked as ${newStatus}`);
      fetchLeads();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead");
    }
  };

  const handleDelete = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Failed to delete lead");
    }
  };

  const handleDeleteAll = async () => {
    try {
      await axios.delete(`${API}/leads`);
      toast.success("All leads deleted");
      fetchLeads();
    } catch (error) {
      console.error("Error deleting all leads:", error);
      toast.error("Failed to delete leads");
    }
  };

  const handleDeleteTestData = async () => {
    try {
      await axios.delete(`${API}/admin/test-data?password=IonSolar2025!`);
      toast.success("Test data deleted");
      fetchLeads();
    } catch (error) {
      console.error("Error deleting test data:", error);
      toast.error("Failed to delete test data");
    }
  };

  const handleExportCsv = () => {
    const cols = ["created_at", "first_name", "last_name", "email", "phone", "address", "city", "state", "zip_code", "service_area", "power_company", "roof_type", "is_homeowner", "credit_score_range", "has_tree_shading", "status", "score", "is_qualified", "booking_datetime_eastern", "notes"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.filter(l => !l.is_test).map(l => cols.map(c => esc(l[c])).join(","));
    const blob = new Blob([[cols.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ion-solar-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const testLeadsCount = leads.filter(l => l.is_test).length;

  const handleSaveNotes = async () => {
    if (!selectedLead) return;
    
    try {
      await axios.patch(`${API}/leads/${selectedLead.id}`, { notes });
      toast.success("Notes saved");
      setSelectedLead(null);
      setNotes("");
      fetchLeads();
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const qualifiedCount = leads.filter(l => l.is_qualified).length;
  const disqualifiedCount = leads.filter(l => !l.is_qualified).length;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="leads-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold text-slate-900">Leads</h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">Manage and track your solar leads</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {testLeadsCount > 0 && (
            <Button 
              onClick={handleDeleteTestData}
              variant="outline"
              className="text-yellow-700 border-yellow-300 hover:bg-yellow-50"
              data-testid="delete-test-data-btn"
            >
              🧪 Delete Test Data ({testLeadsCount})
            </Button>
          )}
          {leads.length > 0 && (
            <Button
              onClick={handleExportCsv}
              variant="outline"
              data-testid="export-leads-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          {leads.length > 0 && (
            <Button 
              onClick={handleDeleteAll}
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="delete-all-leads-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete All
            </Button>
          )}
          <Button 
            onClick={() => window.open("/", "_blank")}
            className="bg-[#A94442] hover:bg-[#8B3836] rounded-full w-full sm:w-auto"
            data-testid="share-form-btn"
          >
            Share Lead Form
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6 md:mb-8">
        <Card className="border-slate-100">
          <CardContent className="p-3 md:p-4 flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-slate-500">Total Leads</p>
              <p className="text-lg md:text-2xl font-['Outfit'] font-bold">{leads.length}</p>
            </div>
            <div className="hidden md:flex w-10 h-10 rounded-full bg-slate-100 items-center justify-center">
              <Filter className="w-5 h-5 text-slate-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-3 md:p-4 flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-slate-500">Qualified</p>
              <p className="text-lg md:text-2xl font-['Outfit'] font-bold text-[#1A4D2E]">{qualifiedCount}</p>
            </div>
            <div className="hidden md:flex w-10 h-10 rounded-full bg-[#1A4D2E]/10 items-center justify-center">
              <UserCheck className="w-5 h-5 text-[#1A4D2E]" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-3 md:p-4 flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-slate-500">Disqualified</p>
              <p className="text-lg md:text-2xl font-['Outfit'] font-bold text-[#A94442]">{disqualifiedCount}</p>
            </div>
            <div className="hidden md:flex w-10 h-10 rounded-full bg-[#A94442]/10 items-center justify-center">
              <UserX className="w-5 h-5 text-[#A94442]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-100 mb-4 md:mb-6">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
                data-testid="search-leads-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 h-11" data-testid="status-filter-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="disqualified">Disqualified</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-100" data-testid="leads-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : filteredLeads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Lead</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Phone</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Score</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Booking</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <LeadRow
                      key={lead.id}
                      lead={lead}
                      onView={(l) => {
                        setSelectedLead(l);
                        setNotes(l.notes || "");
                      }}
                      onDelete={handleDelete}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Filter className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No leads found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lead Details Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Customer Information</DialogTitle>
            <DialogDescription className="sr-only">Lead details including contact info, booking status, and property information</DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-6 pt-4">
              {/* Header */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                  <span className="text-xl font-semibold text-slate-600">
                    {selectedLead.first_name[0]}{selectedLead.last_name[0]}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{selectedLead.first_name} {selectedLead.last_name}</h3>
                  <div className="flex items-center gap-4 mt-1 flex-wrap">
                    <Badge className={`${statusStyles[selectedLead.status]?.bg} ${statusStyles[selectedLead.status]?.text} border-0`}>
                      {statusStyles[selectedLead.status]?.label}
                    </Badge>
                    <span className="text-sm text-slate-500">Score: <span className="font-bold">{selectedLead.score}</span></span>
                    {/* Email notification status */}
                    {selectedLead.email_notification_sent === false && (
                      <span className="flex items-center gap-1 text-xs text-amber-600">
                        <AlertTriangle className="w-3 h-3" />
                        Email failed
                      </span>
                    )}
                    {selectedLead.email_notification_sent === true && (
                      <span className="flex items-center gap-1 text-xs text-[#1A4D2E]">
                        <CheckCircle className="w-3 h-3" />
                        Email sent
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Booked Appointment */}
              {selectedLead.booking_datetime_eastern ? (
                <div className="bg-[#1A4D2E]/10 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[#1A4D2E] mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-[#1A4D2E] uppercase tracking-wide">Booked Appointment</Label>
                        <Badge className={`${bookingStatusStyles[selectedLead.booking_status]?.bg || ''} ${bookingStatusStyles[selectedLead.booking_status]?.text || ''} border-0 text-xs`}>
                          {selectedLead.booking_status === "confirmed" ? "Confirmed" : "Requested"}
                        </Badge>
                      </div>
                      <p className="font-semibold text-[#1A4D2E] text-lg mt-1">
                        {selectedLead.booking_datetime_eastern}
                      </p>
                      {selectedLead.booking_calendar_created && (
                        <p className="text-xs text-[#1A4D2E]/70 mt-1">✓ Added to calendar</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : selectedLead.preferred_date ? (
                <div className="bg-slate-100 rounded-xl p-4">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Preferred Consultation (Not Booked)</Label>
                  <p className="font-semibold text-slate-700 text-lg mt-1">
                    {selectedLead.preferred_date} at {selectedLead.preferred_time || "TBD"}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Lead has not completed the booking step</p>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-slate-500 text-sm">No appointment scheduled</p>
                </div>
              )}

              {/* Contact Information */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 rounded-xl p-4">
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <p className="font-medium">{selectedLead.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <p className="font-medium">{selectedLead.phone}</p>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">Address</Label>
                    <p className="font-medium">{selectedLead.address}</p>
                    <p className="text-slate-600">{selectedLead.city}, {selectedLead.state} {selectedLead.zip_code}</p>
                  </div>
                </div>
              </div>

              {/* Electricity Bill */}
              {selectedLead.bill_file_path ? (
                <div className="bg-[#1A4D2E]/5 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#1A4D2E]" />
                      <div>
                        <Label className="text-xs text-[#1A4D2E] uppercase tracking-wide">Electricity Bill</Label>
                        <p className="font-medium text-slate-900">{selectedLead.bill_filename || "Bill uploaded"}</p>
                      </div>
                    </div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/leads/bill/${selectedLead.bill_file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-[#1A4D2E] text-white rounded-full text-sm font-medium hover:bg-[#1A4D2E]/90 transition-colors"
                      data-testid="download-bill-btn"
                    >
                      <Download className="w-4 h-4" />
                      View Bill
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div>
                      <Label className="text-xs text-slate-400 uppercase tracking-wide">Electricity Bill</Label>
                      <p className="text-sm text-slate-500">No bill uploaded</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pending Confirmation Warning */}
              {selectedLead.pending_confirmation && (
                <div className="bg-[#FDB813]/10 rounded-xl p-4 border border-[#FDB813]/30">
                  <Label className="text-xs text-amber-800 uppercase tracking-wide">Pending Confirmation</Label>
                  <p className="font-medium text-amber-900 mt-1">
                    This lead has a co-op power company. Review their bill to confirm eligibility.
                  </p>
                </div>
              )}

              {/* Service Area & Power Company */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <Label className="text-xs text-slate-500">Service Area</Label>
                  <p className="font-medium text-slate-900">{selectedLead.service_area || "N/A"}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Label className="text-xs text-slate-500">Power Company</Label>
                  <p className="font-medium text-slate-900 capitalize">{selectedLead.power_company?.replace(/_/g, " ") || "N/A"}</p>
                </div>
              </div>

              {/* Property Details */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Property Details
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-slate-50 rounded-xl p-4">
                  <div>
                    <Label className="text-xs text-slate-500">Homeowner</Label>
                    <p className="font-medium">{selectedLead.is_homeowner ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Roof Type</Label>
                    <p className="font-medium capitalize">{selectedLead.roof_type?.replace(/_/g, " ") || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Credit Score Range</Label>
                    <p className="font-medium">{selectedLead.credit_score_range || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">On-Time Payments</Label>
                    <p className="font-medium">{selectedLead.makes_on_time_payments ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Lead Score</Label>
                    <p className={`font-bold text-lg ${selectedLead.score >= 70 ? "text-[#1A4D2E]" : selectedLead.score >= 40 ? "text-amber-600" : "text-[#A94442]"}`}>
                      {selectedLead.score}/100
                    </p>
                  </div>
                </div>
              </div>

              {/* Disqualification Reason */}
              {selectedLead.disqualification_reason && !selectedLead.pending_confirmation && (
                <div className="bg-[#A94442]/10 rounded-xl p-4">
                  <Label className="text-xs text-[#A94442] uppercase tracking-wide">Disqualification Reason</Label>
                  <p className="font-medium text-[#A94442] mt-1">{selectedLead.disqualification_reason}</p>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(`tel:${selectedLead.phone}`)}
                  className="gap-2"
                >
                  <Phone className="w-4 h-4" /> Call
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(`mailto:${selectedLead.email}`)}
                  className="gap-2"
                >
                  <Mail className="w-4 h-4" /> Email
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleStatusChange(selectedLead, "contacted")}
                  className="gap-2"
                >
                  Mark Contacted
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleStatusChange(selectedLead, "converted")}
                  className="gap-2 text-[#1A4D2E]"
                >
                  Mark Converted
                </Button>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes" className="font-semibold">Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this lead..."
                  className="mt-2 min-h-[100px]"
                  data-testid="lead-notes-input"
                />
              </div>

              <Button 
                onClick={handleSaveNotes}
                className="w-full bg-[#A94442] hover:bg-[#8B3836]"
                data-testid="save-notes-btn"
              >
                Save Notes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
